import React, { useEffect, useMemo, useState } from "react";

/* =========================
   STYLE
========================= */

const UI = {
  page:
    "min-h-screen bg-[radial-gradient(circle_at_top,#0d1734_0%,#081121_42%,#040814_100%)] text-white",
  shell: "mx-auto max-w-[2020px] px-2 py-2 sm:px-3 md:px-4 xl:px-5",
  top:
    "mb-3 rounded-[18px] border border-white/10 bg-slate-950/75 shadow-[0_14px_42px_rgba(0,0,0,.28)] backdrop-blur-xl",
  topInner:
    "grid gap-3 px-3 py-3 sm:px-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-center",
  eyebrow: "text-[10px] uppercase tracking-[0.3em] text-slate-500",
  headline:
    "mt-1 text-[22px] font-bold leading-none tracking-tight sm:text-[28px] md:text-[34px]",
  sub: "mt-1.5 max-w-3xl text-[12px] text-slate-400 sm:text-[13px] md:text-sm",
  chip:
    "inline-flex items-center rounded-full border border-white/10 bg-white/[0.05] px-2 py-1 text-[10px] font-semibold text-slate-200",
  button:
    "inline-flex items-center justify-center rounded-lg border border-white/10 bg-slate-900/80 px-3 py-2 text-[11px] font-semibold text-slate-200 transition hover:border-white/20 hover:bg-slate-800/90",
  buttonOn:
    "inline-flex items-center justify-center rounded-lg border border-cyan-400/30 bg-cyan-400/12 px-3 py-2 text-[11px] font-semibold text-cyan-200",
  search:
    "w-full rounded-lg border border-white/10 bg-slate-950/90 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none transition focus:border-cyan-400/40",
  board: "grid gap-3",
  gameCard:
    "overflow-hidden rounded-[20px] border border-white/10 bg-slate-950/74 shadow-[0_14px_42px_rgba(0,0,0,.28)] backdrop-blur-md",
  section: "rounded-[14px] border border-white/10 bg-white/[0.03]",
  soft: "rounded-[12px] border border-white/10 bg-white/[0.035]",
  tiny: "text-[10px] uppercase tracking-[0.22em] text-slate-500",
  label: "text-[10px] uppercase tracking-[0.18em] text-slate-500",
  modalBackdrop: "fixed inset-0 z-50 bg-black/65 backdrop-blur-[2px]",
  modalCard:
    "fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-[min(1180px,95vw)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[24px] border border-white/10 bg-[#07101f] shadow-[0_35px_120px_rgba(0,0,0,.65)]",
};

/* =========================
   TEAM META
========================= */

const TEAM_META = {
  ARI: { name: "Diamondbacks", color: "#A71930", accent: "#E3D4AD" },
  ATL: { name: "Braves", color: "#CE1141", accent: "#13274F" },
  BAL: { name: "Orioles", color: "#DF4601", accent: "#000000" },
  BOS: { name: "Red Sox", color: "#BD3039", accent: "#0C2340" },
  CHC: { name: "Cubs", color: "#0E3386", accent: "#CC3433" },
  CWS: { name: "White Sox", color: "#27251F", accent: "#C4CED4" },
  CIN: { name: "Reds", color: "#C6011F", accent: "#000000" },
  CLE: { name: "Guardians", color: "#0C2340", accent: "#E31937" },
  COL: { name: "Rockies", color: "#333366", accent: "#C4CED4" },
  DET: { name: "Tigers", color: "#0C2340", accent: "#FA4616" },
  HOU: { name: "Astros", color: "#002D62", accent: "#EB6E1F" },
  KC: { name: "Royals", color: "#004687", accent: "#BD9B60" },
  KCR: { name: "Royals", color: "#004687", accent: "#BD9B60" },
  LAA: { name: "Angels", color: "#BA0021", accent: "#003263" },
  LAD: { name: "Dodgers", color: "#005A9C", accent: "#EF3E42" },
  MIA: { name: "Marlins", color: "#00A3E0", accent: "#EF3340" },
  MIL: { name: "Brewers", color: "#12284B", accent: "#FFC52F" },
  MIN: { name: "Twins", color: "#002B5C", accent: "#D31145" },
  NYM: { name: "Mets", color: "#002D72", accent: "#FF5910" },
  NYY: { name: "Yankees", color: "#132448", accent: "#C4CED3" },
  OAK: { name: "Athletics", color: "#003831", accent: "#EFB21E" },
  ATH: { name: "Athletics", color: "#003831", accent: "#EFB21E" },
  PHI: { name: "Phillies", color: "#E81828", accent: "#002D72" },
  PIT: { name: "Pirates", color: "#27251F", accent: "#FDB827" },
  SD: { name: "Padres", color: "#2F241D", accent: "#FFC425" },
  SDP: { name: "Padres", color: "#2F241D", accent: "#FFC425" },
  SEA: { name: "Mariners", color: "#0C2C56", accent: "#005C5C" },
  SF: { name: "Giants", color: "#FD5A1E", accent: "#27251F" },
  SFG: { name: "Giants", color: "#FD5A1E", accent: "#27251F" },
  STL: { name: "Cardinals", color: "#C41E3A", accent: "#0C2340" },
  TB: { name: "Rays", color: "#092C5C", accent: "#8FBCE6" },
  TBR: { name: "Rays", color: "#092C5C", accent: "#8FBCE6" },
  TEX: { name: "Rangers", color: "#003278", accent: "#C0111F" },
  TOR: { name: "Blue Jays", color: "#134A8E", accent: "#1D2D5C" },
  WSH: { name: "Nationals", color: "#AB0003", accent: "#14225A" },
  WAS: { name: "Nationals", color: "#AB0003", accent: "#14225A" },
};

const getTeamMeta = (team) =>
  TEAM_META[(team || "").toUpperCase()] || {
    name: team || "Team",
    color: "#1E293B",
    accent: "#334155",
  };

const logoUrl = (team) => {
  const t = (team || "").toLowerCase();
  return `https://a.espncdn.com/i/teamlogos/mlb/500/${t}.png`;
};

/* =========================
   HELPERS
========================= */

const num = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const cleaned = String(value).replace(/[%$,]/g, "").trim();
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
};

const pct = (value, digits = 1) => {
  const parsed = num(value);
  if (parsed === null) return "—";
  return `${parsed.toFixed(digits)}%`;
};

const fmt0 = (value) => {
  const parsed = num(value);
  return parsed === null ? "—" : parsed.toFixed(0);
};

const fmt1 = (value) => {
  const parsed = num(value);
  return parsed === null ? "—" : parsed.toFixed(1);
};

const fmt2 = (value) => {
  const parsed = num(value);
  return parsed === null ? "—" : parsed.toFixed(2);
};

const fmt3 = (value) => {
  const parsed = num(value);
  return parsed === null ? "—" : parsed.toFixed(3);
};

const fmtK = (value) => {
  const parsed = num(value);
  if (parsed === null) return "—";
  return `$${(parsed / 1000).toFixed(1)}k`;
};

const average = (values) => {
  const valid = values.filter((v) => typeof v === "number" && Number.isFinite(v));
  if (!valid.length) return null;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
};

const normalizeName = (name) =>
  (name || "")
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\bjr\b/g, "jr")
    .replace(/\bsr\b/g, "sr")
    .replace(/\biii\b/g, "iii")
    .replace(/\bii\b/g, "ii")
    .replace(/[^a-z0-9 ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const pick = (obj, keys) => {
  if (!obj) return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") return obj[key];
  }
  return undefined;
};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const splitDeltaPercent = (splitValue, overallValue, invert = false) => {
  const split = num(splitValue);
  const overall = num(overallValue);
  if (split === null || overall === null || overall === 0) return null;
  let delta = ((split - overall) / Math.abs(overall)) * 100;
  if (invert) delta = -delta;
  return delta;
};

const formatSignedPct = (value) => {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
};

const cleanText = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") {
    const v = value.trim();
    return v ? v : null;
  }
  if (typeof value === "number") return String(value);
  return null;
};

const getWeatherText = (weather) => {
  if (!weather || typeof weather !== "object") return null;

  const parts = [];
  if (weather.tempText) parts.push(weather.tempText);
  if (weather.windText) parts.push(weather.windText);
  if (weather.windDirection) parts.push(weather.windDirection);
  if (weather.windEffect) parts.push(`Wind ${weather.windEffect}`);
  if (weather.conditions) parts.push(weather.conditions);

  return parts.length ? parts.join(" • ") : null;
};

const getBatterHandedSplit = (batter, oppPitcherHand) => {
  const ph = (oppPitcherHand || batter?.PH || "").toUpperCase();
  return {
    pitcherHand: ph || "?",
    ab: batter?.["H AB"],
    woba: batter?.["H wOBA"],
    iso: batter?.["H ISO"],
    wrcPlus: batter?.["H wRC+"],
    k: batter?.["H K%"],
    bb: batter?.["H BB%"],
  };
};

const teamPairKey = (a, b) =>
  [String(a || "").toUpperCase(), String(b || "").toUpperCase()].sort().join("-");

const sortGames = (games, sortBy) => {
  const list = [...games];

  if (sortBy === "total") {
    return list.sort((a, b) => {
      const aTotal = (num(a.away.total) || 0) + (num(a.home.total) || 0);
      const bTotal = (num(b.away.total) || 0) + (num(b.home.total) || 0);
      return bTotal - aTotal;
    });
  }

  if (sortBy === "time") {
    return list.sort((a, b) => {
      const aTime = a.extra?.gameTimeSort ?? Number.POSITIVE_INFINITY;
      const bTime = b.extra?.gameTimeSort ?? Number.POSITIVE_INFINITY;

      if (aTime !== bTime) return aTime - bTime;
      return a.sortKey.localeCompare(b.sortKey);
    });
  }

  return list.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
};

/* =========================
   UI BITS
========================= */

function ToolbarButton({ active, children, onClick }) {
  return (
    <button onClick={onClick} className={active ? UI.buttonOn : UI.button}>
      {children}
    </button>
  );
}

function DeltaBadge({ value }) {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return (
      <span className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/[0.05] px-1.5 py-0.5 text-[9px] font-semibold text-slate-200">
        —
      </span>
    );
  }

  const neutral = Math.abs(value) < 0.01;
  const positive = value > 0;

  const cls = neutral
    ? "border-white/10 bg-white/[0.05] text-slate-200"
    : positive
      ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-300"
      : "border-red-400/20 bg-red-400/10 text-red-300";

  return (
    <span className={`inline-flex items-center justify-center rounded-full border px-1.5 py-0.5 text-[9px] font-semibold whitespace-nowrap ${cls}`}>
      {formatSignedPct(value)}
    </span>
  );
}

function StatPill({ label, value, tone = "text-white" }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2">
      <div className={UI.label}>{label}</div>
      <div className={`mt-1 text-sm font-semibold ${tone}`}>{value ?? "—"}</div>
    </div>
  );
}

function Modal({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className={UI.modalBackdrop} onClick={onClose} />
      <div className={UI.modalCard}>
        <div className="flex items-center justify-between border-b border-white/10 bg-slate-950/80 px-4 py-3">
          <div className="text-sm font-semibold text-slate-200">Detail View</div>
          <button
            onClick={onClose}
            className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-slate-200 transition hover:bg-white/10"
          >
            Close
          </button>
        </div>
        <div className="max-h-[calc(90vh-58px)] overflow-y-auto p-4">{children}</div>
      </div>
    </>
  );
}

function TeamLogo({ team, size = 40, className = "" }) {
  return (
    <img
      src={logoUrl(team)}
      alt={team}
      width={size}
      height={size}
      className={`object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,.35)] ${className}`}
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  );
}

/* =========================
   PROJECTION MERGE
========================= */

function buildBatterProjectionLookup(rows) {
  const map = new Map();

  safeArray(rows).forEach((row) => {
    const name = normalizeName(pick(row, ["Player", "player", "Name", "name"]));
    const team = String(pick(row, ["Team", "team"]) || "").toUpperCase();
    if (!name) return;

    map.set(`${team}|${name}`, row);
    if (!map.has(`|${name}`)) map.set(`|${name}`, row);
  });

  return map;
}

function buildPitcherProjectionLookup(rows) {
  const map = new Map();

  safeArray(rows).forEach((row) => {
    const name = normalizeName(pick(row, ["player", "Player", "Name", "name"]));
    const team = String(pick(row, ["Team", "team"]) || "").toUpperCase();
    if (!name) return;

    map.set(`${team}|${name}`, row);
    if (!map.has(`|${name}`)) map.set(`|${name}`, row);
  });

  return map;
}

function attachBatterProjection(row, lookup) {
  const name = normalizeName(row?.Player);
  const team = String(row?.Team || "").toUpperCase();
  const proj = lookup.get(`${team}|${name}`) || lookup.get(`|${name}`) || null;
  return { ...row, proj };
}

function attachPitcherProjection(row, lookup) {
  const name = normalizeName(row?.player);
  const team = String(row?.Team || "").toUpperCase();
  const proj = lookup.get(`${team}|${name}`) || lookup.get(`|${name}`) || null;
  return { ...row, proj };
}

/* =========================
   WEATHER EXTRACTION
========================= */

function parseGameTimeValue(value) {
  if (!value) return Number.POSITIVE_INFINITY;

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  const raw = String(value).trim();
  if (!raw) return Number.POSITIVE_INFINITY;

  // 1) Try real date parsing first for full timestamps/date strings
  const asDate = new Date(raw);
  if (!Number.isNaN(asDate.getTime())) {
    return asDate.getHours() * 60 + asDate.getMinutes();
  }

  // 2) Remove timezone labels like ET / EDT / EST
  const cleaned = raw
    .toUpperCase()
    .replace(/\b(ET|EDT|EST|CT|CDT|CST|MT|MDT|MST|PT|PDT|PST)\b/g, "")
    .trim();

  // 3) Match times like:
  // 7 PM
  // 7:07 PM
  // 7:07PM
  // 19:07
  // 19:07:00
  const match = cleaned.match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*([AP]M)?$/i);
  if (!match) return Number.POSITIVE_INFINITY;

  let hour = Number(match[1]);
  const minute = Number(match[2] || 0);
  const meridiem = (match[3] || "").toUpperCase();

  if (meridiem === "PM" && hour !== 12) hour += 12;
  if (meridiem === "AM" && hour === 12) hour = 0;

  return hour * 60 + minute;
}

function formatGameTimeDisplay(value) {
  if (!value) return "";

  const raw = String(value).trim();
  if (!raw) return "";

  const militaryMatch = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (militaryMatch) {
    let hour = Number(militaryMatch[1]);
    const minute = militaryMatch[2];
    const suffix = hour >= 12 ? "PM" : "AM";

    hour = hour % 12;
    if (hour === 0) hour = 12;

    return `${hour}:${minute} ${suffix}`;
  }

  const ampmMatch = raw.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/i);
  if (ampmMatch) {
    const hour = Number(ampmMatch[1]);
    const minute = ampmMatch[2];
    const suffix = ampmMatch[3].toUpperCase();
    return `${hour}:${minute} ${suffix}`;
  }

  return raw;
}

function parseWeatherRow(row) {
  const stadium = cleanText(row?.["Stadium"]);
  const team = cleanText(row?.["Team Abbreviation"])?.toUpperCase() || "";

  const rawTemp = row?.["Temperature (°F)"];
  const rawWind = row?.["Wind Speed (mph)"];
  const rawDirection = cleanText(row?.["Wind Direction"]);
  const rawEffect = cleanText(row?.["Wind Effect"]);
  const rawHumidity = row?.["Humidity (%)"];
  const conditions = cleanText(row?.["Conditions"]);

  const rawGameTime = pick(row, [
    "Game Time",
    "Game Time ET",
    "Game Time (ET)",
    "GameTime",
    "Game_Time",
    "Start Time",
    "Start",
    "First Pitch",
    "First Pitch ET",
    "Time",
  ]);

  const tempNum = num(rawTemp);
  const windNum = num(rawWind);

  const tempText =
    typeof rawTemp === "string" && rawTemp.trim().toLowerCase() === "dome"
      ? "Dome"
      : tempNum !== null
        ? `${fmt0(tempNum)}°`
        : cleanText(rawTemp);

  const windText =
    typeof rawWind === "string" && rawWind.trim().toLowerCase() === "dome"
      ? null
      : windNum !== null
        ? `${fmt1(windNum)} mph`
        : cleanText(rawWind);

  const roof =
    [rawTemp, rawWind, rawDirection, rawEffect, rawHumidity, conditions].some(
      (v) => typeof v === "string" && v.trim().toLowerCase() === "dome"
    )
      ? "Dome"
      : null;

  return {
    team,
    ballpark: stadium,
    gameTime: cleanText(rawGameTime),
    gameTimeSort: parseGameTimeValue(rawGameTime),
    weather: {
      temp: tempNum,
      wind: windNum,
      tempText,
      windText,
      windDirection: roof ? null : rawDirection,
      windEffect: roof ? null : rawEffect,
      conditions: roof ? "Dome" : conditions,
      roof,
    },
  };
}

function buildWeatherLookup(weatherRows) {
  const map = new Map();

  safeArray(weatherRows).forEach((row) => {
    const parsed = parseWeatherRow(row);
    if (!parsed.team) return;
    map.set(parsed.team, parsed);
  });

  return map;
}

/* =========================
   DETAIL PANELS
========================= */

function HitterModal({ batter, oppPitcherHand }) {
  if (!batter) return null;

  const split = getBatterHandedSplit(batter, oppPitcherHand);
  const splitWobaDelta = splitDeltaPercent(split.woba, batter?.wOBA, false);
  const splitIsoDelta = splitDeltaPercent(split.iso, batter?.ISO, false);
  const splitKDelta = splitDeltaPercent(split.k, batter?.["K%"], true);

  return (
    <div className="space-y-4">
      <div className="rounded-[18px] border border-white/10 bg-gradient-to-r from-slate-900 to-slate-950 px-4 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-2xl font-bold">{batter?.Player || "—"}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className={UI.chip}>{batter?.Team || "—"}</span>
              <span className={UI.chip}>Bat {batter?.BH || "—"}</span>
              <span className={UI.chip}>Pitcher {batter?.Pitcher || "—"}</span>
              <span className={UI.chip}>Rating {batter?.Rating || "—"}</span>
              <span className={UI.chip}>DK {fmtK(batter?.DK)}</span>
              <span className={UI.chip}>FD {fmtK(batter?.FD)}</span>
            </div>
          </div>
          <div className={UI.tiny}>Hitter Detail</div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className={UI.soft + " p-3"}>
          <div className="mb-3 text-sm font-semibold text-slate-200">DFS / Matchup</div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            <StatPill label="DK Proj" value={fmt2(batter?.DK_1)} tone="text-cyan-300" />
            <StatPill label="FD Proj" value={fmt2(batter?.FD_1)} tone="text-fuchsia-300" />
            <StatPill label="DK Value" value={fmt2(batter?.Val)} />
            <StatPill label="FD Value" value={fmt2(batter?.Val_1)} />
            <StatPill label="SB/g" value={fmt2(batter?.["SB/g"])} />
            <StatPill label="Team Total" value={batter?.Total || "—"} />
          </div>
        </div>

        <div className={UI.soft + " p-3"}>
          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-200">
            <span>Split vs {split.pitcherHand}HP</span>
            <DeltaBadge value={splitWobaDelta} />
            <DeltaBadge value={splitIsoDelta} />
            <DeltaBadge value={splitKDelta} />
          </div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            <StatPill label="Split AB" value={split.ab || "—"} />
            <StatPill label="Split ISO" value={fmt3(split.iso)} tone="text-amber-300" />
            <StatPill label="Split wOBA" value={fmt3(split.woba)} tone="text-emerald-300" />
            <StatPill label="Split wRC+" value={split.wrcPlus || "—"} />
            <StatPill label="Split K%" value={pct(split.k)} />
            <StatPill label="Split BB%" value={pct(split.bb)} />
          </div>
        </div>
      </div>

      <div className={UI.soft + " p-3"}>
        <div className="mb-3 text-sm font-semibold text-slate-200">Projection Event Lines</div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-6">
          <StatPill label="AB" value={fmt2(batter?.proj?.AB)} />
          <StatPill label="H" value={fmt2(batter?.proj?.H_1)} />
          <StatPill label="1B" value={fmt2(batter?.proj?.["1B"])} />
          <StatPill label="2B" value={fmt2(batter?.proj?.["2B"])} />
          <StatPill label="3B" value={fmt2(batter?.proj?.["3B"])} />
          <StatPill label="HR" value={fmt2(batter?.proj?.HR)} tone="text-rose-300" />
          <StatPill label="RBI" value={fmt2(batter?.proj?.RBI)} />
          <StatPill label="R" value={fmt2(batter?.proj?.R)} />
          <StatPill label="SB" value={fmt2(batter?.proj?.SB)} />
          <StatPill label="BB" value={fmt2(batter?.proj?.BB)} />
          <StatPill label="K" value={fmt2(batter?.proj?.K)} />
        </div>
      </div>

      <div className={UI.soft + " p-3"}>
        <div className="mb-3 text-sm font-semibold text-slate-200">Overall Profile</div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-6">
          <StatPill label="AB" value={batter?.AB || "—"} />
          <StatPill label="ISO" value={fmt3(batter?.ISO)} tone="text-amber-300" />
          <StatPill label="wOBA" value={fmt3(batter?.wOBA)} tone="text-emerald-300" />
          <StatPill label="K%" value={pct(batter?.["K%"])} />
          <StatPill label="BB%" value={pct(batter?.["BB%"])} />
          <StatPill label="HR/FB" value={pct(batter?.["HR/FB"])} />
          <StatPill label="FB%" value={pct(batter?.FB)} />
          <StatPill label="GB%" value={pct(batter?.GB)} />
          <StatPill label="Contact%" value={pct(batter?.["Cnt%"])} />
          <StatPill label="SwStr%" value={pct(batter?.["SwS%"])} />
          <StatPill label="Barrel%" value={pct(batter?.["Bar%"])} />
          <StatPill label="Hard Hit%" value={pct(batter?.["HH%"])} />
          <StatPill label="EV" value={fmt1(batter?.EV)} />
        </div>
      </div>
    </div>
  );
}

function PitcherModal({ pitcher }) {
  if (!pitcher) return null;

  return (
    <div className="space-y-4">
      <div className="rounded-[18px] border border-white/10 bg-gradient-to-r from-slate-900 to-slate-950 px-4 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-2xl font-bold">{pitcher?.player || "—"}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <span className={UI.chip}>{pitcher?.Team || "—"}</span>
              <span className={UI.chip}>
                Throw {pitcher?.Hand || pick(pitcher?.proj, ["H", "Hand"]) || "—"}
              </span>
              <span className={UI.chip}>Opp {pitcher?.Opp || "—"}</span>
              <span className={UI.chip}>Rating {pitcher?.Rating || "—"}</span>
              <span className={UI.chip}>DK {fmtK(pitcher?.DK)}</span>
              <span className={UI.chip}>FD {fmtK(pitcher?.FD)}</span>
            </div>
          </div>
          <div className={UI.tiny}>Pitcher Detail</div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className={UI.soft + " p-3"}>
          <div className="mb-3 text-sm font-semibold text-slate-200">Projection Event Lines</div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <StatPill label="IP" value={fmt2(pitcher?.proj?.IP)} tone="text-cyan-300" />
            <StatPill label="ER" value={fmt2(pitcher?.proj?.ER)} />
            <StatPill label="K" value={fmt2(pitcher?.proj?.K)} tone="text-fuchsia-300" />
            <StatPill label="H" value={fmt2(pitcher?.proj?.H_1)} />
            <StatPill label="BB" value={fmt2(pitcher?.proj?.BB)} />
            <StatPill label="HR" value={fmt2(pitcher?.proj?.HR)} />
            <StatPill label="W" value={fmt2(pitcher?.proj?.W)} />
          </div>
        </div>

        <div className={UI.soft + " p-3"}>
          <div className="mb-3 text-sm font-semibold text-slate-200">DFS / Matchup</div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            <StatPill label="Vegas Total" value={pitcher?.Total || pitcher?.["Imp. Total"] || "—"} />
            <StatPill label="Win%" value={pitcher?.["W%"] || "—"} />
            <StatPill label="K Prop" value={pitcher?.K || "—"} />
            <StatPill
              label="DK Proj"
              value={fmt2(pitcher?.DK_1 || pitcher?.["DK Proj"])}
              tone="text-cyan-300"
            />
            <StatPill
              label="FD Proj"
              value={fmt2(pitcher?.FD_1 || pitcher?.["FD Proj"])}
              tone="text-fuchsia-300"
            />
            <StatPill label="Velo" value={fmt1(pitcher?.Velo)} />
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <div className={UI.soft + " p-3"}>
          <div className="mb-3 text-sm font-semibold text-slate-200">Pitcher Skill</div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            <StatPill label="xFIP" value={fmt2(pitcher?.xFIP)} tone="text-cyan-300" />
            <StatPill label="K%" value={pct(pitcher?.["K%"])} />
            <StatPill label="SwStr%" value={pct(pitcher?.["SwS%"])} />
            <StatPill label="BB%" value={pct(pitcher?.["BB%"])} />
            <StatPill label="K/9" value={fmt1(pitcher?.["K/9"])} />
            <StatPill label="BB/9" value={fmt1(pitcher?.["BB/9"])} />
            <StatPill label="HR/9" value={fmt1(pitcher?.["HR/9"])} />
            <StatPill label="GB%" value={pct(pitcher?.["GB%"])} />
            <StatPill label="FB%" value={pct(pitcher?.["FB%"])} />
            <StatPill label="HH%" value={pct(pitcher?.["HH%"])} />
            <StatPill label="Barrel%" value={pct(pitcher?.["Bar%"])} />
            <StatPill label="EV" value={fmt1(pitcher?.EV)} />
          </div>
        </div>

        <div className={UI.soft + " p-3"}>
          <div className="mb-3 text-sm font-semibold text-slate-200">Opponent Profile</div>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            <StatPill label="Opp K%" value={pct(pitcher?.["opp K%"])} />
            <StatPill label="Opp BB%" value={pct(pitcher?.["opp BB%"])} />
            <StatPill label="Opp wOBA" value={fmt3(pitcher?.["opp wOBA"])} />
            <StatPill label="Opp ISO" value={fmt3(pitcher?.["opp ISO"])} />
            <StatPill label="Opp wRC+" value={pitcher?.["opp wRC+"] || "—"} />
            <StatPill label="Slate Score" value={fmt1(pitcher?.["0"])} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   BUILD GAMES
========================= */

function buildGamesFromData({ batterRows, pitcherRows, weatherLookup }) {
  const batters = safeArray(batterRows);
  const pitchers = safeArray(pitcherRows);

  const teamToLineup = {};
  batters.forEach((b) => {
    const team = String(b?.Team || "").toUpperCase();
    if (!team) return;

    if (!teamToLineup[team]) teamToLineup[team] = [];
    teamToLineup[team].push({
      order: Number(b?.BO || 99),
      name: b?.Player || "",
      hand: b?.BH || "",
      dkProj: num(b?.DK_1),
      fdProj: num(b?.FD_1),
      dkSalary: num(b?.DK),
      fdSalary: num(b?.FD),
      raw: b,
    });
  });

  Object.keys(teamToLineup).forEach((team) => {
    teamToLineup[team].sort((a, b) => a.order - b.order);
  });

  const pitcherByTeam = {};
  pitchers.forEach((p) => {
    const team = String(p?.Team || "").toUpperCase();
    if (team) pitcherByTeam[team] = p;
  });

  const used = new Set();
  const games = [];

  const getSiteCode = (primaryPitcher, secondaryPitcher, awayCode, homeCode) => {
    const raw =
      primaryPitcher?.Park ||
      secondaryPitcher?.Park ||
      primaryPitcher?.Field ||
      secondaryPitcher?.Field ||
      homeCode ||
      awayCode ||
      "";

    return String(raw).toUpperCase().trim();
  };

  pitchers.forEach((p) => {
    const team = String(p?.Team || "").toUpperCase();
    const opp = String(p?.Opp || "").toUpperCase();
    if (!team || !opp) return;

    const key = teamPairKey(team, opp);
    if (used.has(key)) return;

    const p2 = pitcherByTeam[opp];
    if (!p2) return;

    used.add(key);

    const siteCode = getSiteCode(p, p2, team, opp);

    const siteWeather = weatherLookup.get(siteCode) || null;
    const awayWeather = weatherLookup.get(team) || null;
    const homeWeather = weatherLookup.get(opp) || null;

    const awayLooksAway =
      siteCode &&
      (p?.Park || p?.Field) &&
      String(p?.Park || p?.Field).toUpperCase() !== team;

    const homeLooksHome =
      siteCode &&
      (p2?.Park || p2?.Field) &&
      String(p2?.Park || p2?.Field).toUpperCase() === opp;

    const awayCode = awayLooksAway || homeLooksHome ? team : opp;
    const homeCode = awayLooksAway || homeLooksHome ? opp : team;

    const awayPitcher = awayCode === team ? p : p2;
    const homePitcher = homeCode === opp ? p2 : p;

    games.push({
      id: key,
      sortKey: `${awayCode}-${homeCode}`,
      extra: {
        siteCode,
        weather: siteWeather?.weather || homeWeather?.weather || awayWeather?.weather || null,
        ballpark:
          siteWeather?.ballpark ||
          homeWeather?.ballpark ||
          awayWeather?.ballpark ||
          null,
        gameTime:
          siteWeather?.gameTime ||
          homeWeather?.gameTime ||
          awayWeather?.gameTime ||
          homePitcher?.Time ||
          awayPitcher?.Time ||
          null,
        gameTimeSort: Number.isFinite(siteWeather?.gameTimeSort)
          ? siteWeather.gameTimeSort
          : Number.isFinite(homeWeather?.gameTimeSort)
            ? homeWeather.gameTimeSort
            : Number.isFinite(awayWeather?.gameTimeSort)
              ? awayWeather.gameTimeSort
              : parseGameTimeValue(
                  siteWeather?.gameTime ||
                    homeWeather?.gameTime ||
                    awayWeather?.gameTime ||
                    homePitcher?.Time ||
                    awayPitcher?.Time
                ),
      },
      away: {
        code: awayCode,
        meta: getTeamMeta(awayCode),
        total:
          homePitcher?.Total ||
          homePitcher?.["Imp. Total"] ||
          homePitcher?.proj?.["Imp. Total"] ||
          "—",
        pitcher: awayPitcher,
        pitcherName: awayPitcher?.player || "",
        pitcherHand: awayPitcher?.Hand || awayPitcher?.proj?.H || "",
        lineup: teamToLineup[awayCode] || [],
      },
      home: {
        code: homeCode,
        meta: getTeamMeta(homeCode),
        total:
          awayPitcher?.Total ||
          awayPitcher?.["Imp. Total"] ||
          awayPitcher?.proj?.["Imp. Total"] ||
          "—",
        pitcher: homePitcher,
        pitcherName: homePitcher?.player || "",
        pitcherHand: homePitcher?.Hand || homePitcher?.proj?.H || "",
        lineup: teamToLineup[homeCode] || [],
      },
    });
  });

  if (!games.length) {
    const teams = Object.keys(teamToLineup);

    for (let i = 0; i < teams.length; i += 2) {
      const t1 = teams[i];
      const t2 = teams[i + 1];
      if (!t1 || !t2) continue;

      const p1 = pitcherByTeam[t1] || null;
      const p2 = pitcherByTeam[t2] || null;

      const siteCode = getSiteCode(p1, p2, t1, t2);
      const siteWeather = weatherLookup.get(siteCode) || null;
      const w1 = weatherLookup.get(t1) || null;
      const w2 = weatherLookup.get(t2) || null;

      const awayLooksAway =
        siteCode &&
        (p1?.Park || p1?.Field) &&
        String(p1?.Park || p1?.Field).toUpperCase() !== t1;

      const homeLooksHome =
        siteCode &&
        (p2?.Park || p2?.Field) &&
        String(p2?.Park || p2?.Field).toUpperCase() === t2;

      const awayCode = awayLooksAway || homeLooksHome ? t1 : t2;
      const homeCode = awayLooksAway || homeLooksHome ? t2 : t1;

      const awayPitcher = awayCode === t1 ? p1 : p2;
      const homePitcher = homeCode === t2 ? p2 : p1;

      games.push({
        id: teamPairKey(t1, t2),
        sortKey: `${awayCode}-${homeCode}`,
        extra: {
          siteCode,
          weather: siteWeather?.weather || w2?.weather || w1?.weather || null,
          ballpark: siteWeather?.ballpark || w2?.ballpark || w1?.ballpark || null,
          gameTime:
            siteWeather?.gameTime ||
            w2?.gameTime ||
            w1?.gameTime ||
            homePitcher?.Time ||
            awayPitcher?.Time ||
            null,
          gameTimeSort: Number.isFinite(siteWeather?.gameTimeSort)
            ? siteWeather.gameTimeSort
            : Number.isFinite(w2?.gameTimeSort)
              ? w2.gameTimeSort
              : Number.isFinite(w1?.gameTimeSort)
                ? w1.gameTimeSort
                : parseGameTimeValue(
                    siteWeather?.gameTime ||
                      w2?.gameTime ||
                      w1?.gameTime ||
                      homePitcher?.Time ||
                      awayPitcher?.Time
                  ),
        },
        away: {
          code: awayCode,
          meta: getTeamMeta(awayCode),
          total:
            homePitcher?.Total ||
            homePitcher?.["Imp. Total"] ||
            homePitcher?.proj?.["Imp. Total"] ||
            "—",
          pitcher: awayPitcher,
          pitcherName: awayPitcher?.player || "",
          pitcherHand: awayPitcher?.Hand || awayPitcher?.proj?.H || "",
          lineup: teamToLineup[awayCode] || [],
        },
        home: {
          code: homeCode,
          meta: getTeamMeta(homeCode),
          total:
            awayPitcher?.Total ||
            awayPitcher?.["Imp. Total"] ||
            awayPitcher?.proj?.["Imp. Total"] ||
            "—",
          pitcher: homePitcher,
          pitcherName: homePitcher?.player || "",
          pitcherHand: homePitcher?.Hand || homePitcher?.proj?.H || "",
          lineup: teamToLineup[homeCode] || [],
        },
      });
    }
  }

  return games.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
}

/* =========================
   MATCHUP UI
========================= */

function getTopBats(team, metricMode) {
  const lineup = safeArray(team?.lineup);

  const ranked = [...lineup].sort((a, b) => {
    if (metricMode === "stats") {
      const aEdge = splitDeltaPercent(a.raw?.["H wOBA"], a.raw?.wOBA, false) ?? -999;
      const bEdge = splitDeltaPercent(b.raw?.["H wOBA"], b.raw?.wOBA, false) ?? -999;
      return bEdge - aEdge;
    }
    return (num(b.dkProj) || 0) - (num(a.dkProj) || 0);
  });

  return ranked.slice(0, 3);
}

function MatchupHeader({ game }) {
  const away = game.away;
  const home = game.home;
  const weatherText = getWeatherText(game.extra?.weather);
  const ballparkText = cleanText(game.extra?.ballpark);
  const gameTimeText = formatGameTimeDisplay(game.extra?.gameTime);
  const combinedTotal = (num(away.total) || 0) + (num(home.total) || 0);

  return (
    <div
      className="relative overflow-hidden border-b border-white/10"
      style={{
        background: `
          linear-gradient(
            90deg,
            ${away.meta.color}14 0%,
            ${away.meta.color}24 10%,
            ${away.meta.color}42 22%,
            ${away.meta.color}70 36%,
            rgba(5,9,20,.95) 47%,
            rgba(5,9,20,.95) 53%,
            ${home.meta.color}70 64%,
            ${home.meta.color}42 78%,
            ${home.meta.color}24 90%,
            ${home.meta.color}14 100%
          )
        `,
      }}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(
                90deg,
                transparent 0%,
                ${away.meta.color}22 22%,
                ${away.meta.color}35 40%,
                transparent 50%,
                ${home.meta.color}35 60%,
                ${home.meta.color}22 78%,
                transparent 100%
              )
            `,
          }}
        />

        <div className="absolute left-2 top-1/2 hidden -translate-y-1/2 opacity-[0.04] sm:block">
          <TeamLogo team={away.code} size={92} />
        </div>
        <div className="absolute right-2 top-1/2 hidden -translate-y-1/2 opacity-[0.04] sm:block">
          <TeamLogo team={home.code} size={92} />
        </div>
      </div>

      <div className="relative px-3 py-3 sm:px-4">
        <div className="grid gap-3 lg:grid-cols-[1fr_auto_1fr] lg:items-center">
          <div className="flex min-w-0 items-center gap-3">
            <TeamLogo team={away.code} size={30} />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-lg font-bold">{away.code}</div>
                <span className={UI.chip}>Imp {away.total ?? "—"}</span>
              </div>
              <div className="truncate text-[11px] text-slate-400">
                {away.pitcherName || "—"} {away.pitcherHand ? `(${away.pitcherHand})` : ""}
              </div>
            </div>
          </div>

          <div className="text-left lg:text-center">
            <div className="text-[19px] font-bold tracking-tight sm:text-[21px]">
              {away.code} @ {home.code}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 lg:justify-center">
              {gameTimeText ? <span className={UI.chip}>{gameTimeText}</span> : null}
              <span className={UI.chip}>Game Total {fmt1(combinedTotal)}</span>
              {weatherText ? <span className={UI.chip}>{weatherText}</span> : null}
              {ballparkText ? <span className={UI.chip}>{ballparkText}</span> : null}
            </div>
          </div>

          <div className="flex min-w-0 items-center gap-3 lg:justify-end">
            <div className="min-w-0 text-left lg:text-right">
              <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                <span className={UI.chip}>Imp {home.total ?? "—"}</span>
                <div className="text-lg font-bold">{home.code}</div>
              </div>
              <div className="truncate text-[11px] text-slate-400">
                {home.pitcherName || "—"} {home.pitcherHand ? `(${home.pitcherHand})` : ""}
              </div>
            </div>
            <TeamLogo team={home.code} size={30} />
          </div>
        </div>
      </div>
    </div>
  );
}

function TeamSummaryCard({ team, metricMode, onSelectPitcher, onSelectHitter }) {
  const topBats = getTopBats(team, metricMode);
  const pitcher = team.pitcher;
  const proj = pitcher?.proj;

  return (
    <div className={UI.section + " p-2.5"}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <TeamLogo team={team.code} size={28} />
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-[15px] font-bold">{team.code}</div>
              <span className={UI.chip}>Imp {team.total ?? "—"}</span>
            </div>
            <div className="text-[11px] text-slate-400">{team.meta.name}</div>
          </div>
        </div>

        <button
          onClick={() => onSelectPitcher(team.pitcher)}
          className="rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-2 text-left transition hover:bg-white/[0.08]"
        >
          <div className={UI.tiny}>Starter</div>
          <div className="mt-0.5 text-[12px] font-semibold text-white">
            {team.pitcherName || "—"} {team.pitcherHand ? `(${team.pitcherHand})` : ""}
          </div>
          <div className="mt-1 text-[10px] text-slate-300">
            {metricMode === "stats"
              ? `xFIP ${fmt2(pitcher?.xFIP)} • K% ${pct(pitcher?.["K%"])} • HR/9 ${fmt1(pitcher?.["HR/9"])}`
              : `IP ${fmt2(proj?.IP)} • K ${fmt2(proj?.K)} • ER ${fmt2(proj?.ER)}`}
          </div>
        </button>
      </div>

      <div className="mt-2.5">
        <div className={UI.tiny}>Top Bats</div>
        <div className="mt-1.5 grid gap-2 sm:grid-cols-3">
          {topBats.map((row) => {
            const edge = splitDeltaPercent(row.raw?.["H wOBA"], row.raw?.wOBA, false);

            return (
              <button
                key={`${team.code}-${row.order}-${row.name}`}
                onClick={() => onSelectHitter(row.raw)}
                className="rounded-lg border border-white/10 bg-white/[0.035] px-2.5 py-2 text-left transition hover:border-cyan-400/25 hover:bg-white/[0.06]"
              >
                <div className="truncate text-[12px] font-semibold text-white">
                  {row.order}. {row.name}
                </div>
                <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-slate-400">
                  <span>{row.hand || "—"}</span>
                  {metricMode === "stats" ? (
                    <span className="font-semibold text-emerald-300">{formatSignedPct(edge)}</span>
                  ) : (
                    <span className="font-semibold text-cyan-300">{fmt2(row.dkProj)}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TableCell({ children, className = "" }) {
  return <div className={`px-1 py-1.5 ${className}`}>{children}</div>;
}

function TeamLineupPanel({ team, oppPitcherHand, metricMode, onSelectHitter }) {
  const lineup = team.lineup || [];

const avgRow = {
  dkProj: average(lineup.map((x) => num(x.dkProj))),
  fdProj: average(lineup.map((x) => num(x.fdProj))),
  woba: average(lineup.map((x) => num(x.raw?.wOBA))),
  iso: average(lineup.map((x) => num(x.raw?.ISO))),
  splitWoba: average(lineup.map((x) => num(x.raw?.["H wOBA"]))),
  splitIso: average(lineup.map((x) => num(x.raw?.["H ISO"]))),
  hits: average(lineup.map((x) => num(x.raw?.proj?.H_1))),
  hr: average(lineup.map((x) => num(x.raw?.proj?.HR))),
  rbi: average(lineup.map((x) => num(x.raw?.proj?.RBI))),
  runs: average(lineup.map((x) => num(x.raw?.proj?.R))),
  sb: average(lineup.map((x) => num(x.raw?.proj?.SB))),
};

  const tableColsProj =
    "grid-cols-[24px_minmax(96px,1.12fr)_20px_44px_44px_34px_34px_36px_30px_28px]";
  const tableColsStats =
    "grid-cols-[24px_minmax(96px,1.06fr)_20px_42px_42px_42px_42px_46px_44px_54px_54px_50px]";

  return (
    <div className={UI.section + " overflow-hidden"}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-2 py-2">
        <div className="flex items-center gap-2">
          <TeamLogo team={team.code} size={20} />
          <div>
            <div className="text-[13px] font-semibold text-slate-200">{team.code} Lineup</div>
            <div className="text-[10px] text-slate-400">
              {metricMode === "proj" ? "Projection" : `vs ${oppPitcherHand || "?"}HP`}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <span className={UI.chip}>Avg DK {fmt2(avgRow.dkProj)}</span>
          <span className={UI.chip}>Avg FD {fmt2(avgRow.fdProj)}</span>
        </div>
      </div>

      <div className="w-full overflow-hidden">
        <div className="w-full">
          <div
            className={`grid items-center border-b border-white/10 bg-slate-900/95 text-[9px] uppercase tracking-[0.1em] text-slate-400 ${
              metricMode === "proj" ? tableColsProj : tableColsStats
            }`}
          >
            <TableCell>#</TableCell>
            <TableCell className="truncate">Player</TableCell>
            <TableCell className="text-center">H</TableCell>
            <TableCell className="text-right">DK</TableCell>
            <TableCell className="text-right">FD</TableCell>

            {metricMode === "proj" ? (
              <>
                <TableCell className="text-right">H</TableCell>
                <TableCell className="text-right">HR</TableCell>
                <TableCell className="text-right">RBI</TableCell>
                <TableCell className="text-right">R</TableCell>
                <TableCell className="text-right">SB</TableCell>
              </>
            ) : (
              <>
                <TableCell className="text-right">wOBA</TableCell>
                <TableCell className="text-right">ISO</TableCell>
                <TableCell className="text-right">Spl wOBA</TableCell>
                <TableCell className="text-right">Spl ISO</TableCell>
                <TableCell className="text-right">wOBA Δ</TableCell>
                <TableCell className="text-right">ISO Δ</TableCell>
                <TableCell className="text-right">K% Δ</TableCell>
              </>
            )}
          </div>

          {lineup.map((row, idx) => {
            const splitWobaDelta = splitDeltaPercent(row.raw?.["H wOBA"], row.raw?.wOBA, false);
            const splitIsoDelta = splitDeltaPercent(row.raw?.["H ISO"], row.raw?.ISO, false);
            const splitKDelta = splitDeltaPercent(row.raw?.["H K%"], row.raw?.["K%"], true);

            return (
              <button
                key={`${team.code}-${row.order}-${row.name}`}
                onClick={() => onSelectHitter(row.raw)}
                className={`grid w-full items-center border-b border-white/10 text-left text-[11px] text-slate-200 transition hover:bg-cyan-400/[0.06] ${
                  idx % 2 === 0 ? "bg-white/[0.02]" : "bg-transparent"
                } ${metricMode === "proj" ? tableColsProj : tableColsStats}`}
              >
                <TableCell>
                  <span className="font-semibold text-slate-300">{row.order}</span>
                </TableCell>

                <TableCell className="min-w-0">
                  <div className="truncate font-semibold text-white">{row.name}</div>
                </TableCell>

                <TableCell className="text-center">
                  <span className="text-slate-400">{row.hand || "—"}</span>
                </TableCell>

                <TableCell className="text-right font-semibold text-cyan-300">
                  {fmt2(row.dkProj)}
                </TableCell>

                <TableCell className="text-right font-semibold text-fuchsia-300">
                  {fmt2(row.fdProj)}
                </TableCell>

                {metricMode === "proj" ? (
                  <>
                    <TableCell className="text-right">{fmt2(row.raw?.proj?.H_1)}</TableCell>
                    <TableCell className="text-right text-rose-300">{fmt2(row.raw?.proj?.HR)}</TableCell>
                    <TableCell className="text-right">{fmt2(row.raw?.proj?.RBI)}</TableCell>
                    <TableCell className="text-right">{fmt2(row.raw?.proj?.R)}</TableCell>
                    <TableCell className="text-right">{fmt2(row.raw?.proj?.SB)}</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell className="text-right">{fmt3(row.raw?.wOBA)}</TableCell>
                    <TableCell className="text-right">{fmt3(row.raw?.ISO)}</TableCell>
                    <TableCell className="text-right">{fmt3(row.raw?.["H wOBA"])}</TableCell>
                    <TableCell className="text-right">{fmt3(row.raw?.["H ISO"])}</TableCell>
                    <TableCell className="flex justify-end"><DeltaBadge value={splitWobaDelta} /></TableCell>
                    <TableCell className="flex justify-end"><DeltaBadge value={splitIsoDelta} /></TableCell>
                    <TableCell className="flex justify-end"><DeltaBadge value={splitKDelta} /></TableCell>
                  </>
                )}
              </button>
            );
          })}

          <div
            className={`grid items-center bg-slate-900/75 text-[11px] font-semibold text-white ${
              metricMode === "proj" ? tableColsProj : tableColsStats
            }`}
          >
            <TableCell />
            <TableCell className="truncate">Avg</TableCell>
            <TableCell />
            <TableCell className="text-right text-cyan-300">{fmt2(avgRow.dkProj)}</TableCell>
            <TableCell className="text-right text-fuchsia-300">{fmt2(avgRow.fdProj)}</TableCell>

            {metricMode === "proj" ? (
              <>
                <TableCell className="text-right">{fmt2(avgRow.hits)}</TableCell>
                <TableCell className="text-right">{fmt2(avgRow.hr)}</TableCell>
                <TableCell className="text-right">{fmt2(avgRow.rbi)}</TableCell>
                <TableCell className="text-right">{fmt2(avgRow.runs)}</TableCell>
                <TableCell className="text-right">{fmt2(avgRow.sb)}</TableCell>
              </>
            ) : (
              <>
                <TableCell className="text-right">{fmt3(avgRow.woba)}</TableCell>
                <TableCell className="text-right">{fmt3(avgRow.iso)}</TableCell>
                <TableCell className="text-right">{fmt3(avgRow.splitWoba)}</TableCell>
                <TableCell className="text-right">{fmt3(avgRow.splitIso)}</TableCell>
                <TableCell className="text-right">—</TableCell>
                <TableCell className="text-right">—</TableCell>
                <TableCell className="text-right">—</TableCell>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function GameCard({ game, metricMode, onSelectHitter, onSelectPitcher }) {
  return (
    <div className={UI.gameCard}>
      <MatchupHeader game={game} />

      <div className="grid gap-2 p-2.5 xl:grid-cols-2">
        <TeamSummaryCard
          team={game.away}
          metricMode={metricMode}
          onSelectPitcher={onSelectPitcher}
          onSelectHitter={onSelectHitter}
        />
        <TeamSummaryCard
          team={game.home}
          metricMode={metricMode}
          onSelectPitcher={onSelectPitcher}
          onSelectHitter={onSelectHitter}
        />
      </div>

      <div className="grid gap-2 px-2.5 pb-2.5 xl:grid-cols-2">
        <TeamLineupPanel
          team={game.away}
          oppPitcherHand={game.home.pitcherHand}
          metricMode={metricMode}
          onSelectHitter={onSelectHitter}
        />
        <TeamLineupPanel
          team={game.home}
          oppPitcherHand={game.away.pitcherHand}
          metricMode={metricMode}
          onSelectHitter={onSelectHitter}
        />
      </div>
    </div>
  );
}

/* =========================
   MAIN
========================= */

export default function MlbMatchups() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [batterData, setBatterData] = useState([]);
  const [pitcherData, setPitcherData] = useState([]);
  const [batterProjData, setBatterProjData] = useState([]);
  const [pitcherProjData, setPitcherProjData] = useState([]);
  const [weatherGameTimeData, setWeatherGameTimeData] = useState([]);

  const [metricMode, setMetricMode] = useState("proj");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("time");
  const [selectedDetail, setSelectedDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadJson(path, required = false) {
      try {
        const res = await fetch(path, { cache: "no-store" });
        if (!res.ok) {
          if (required) throw new Error(`Failed to load ${path} (${res.status})`);
          return [];
        }
        return await res.json();
      } catch (err) {
        if (required) throw err;
        return [];
      }
    }

    async function load() {
      try {
        setLoading(true);
        setError("");

        const [batters, pitchers, batterProj, pitcherProj, weatherGameTime] = await Promise.all([
          loadJson("/data/mlb/latest/batter_data.json", true),
          loadJson("/data/mlb/latest/pitcher_data.json", true),
          loadJson("/data/mlb/latest/batter_projections.json", false),
          loadJson("/data/mlb/latest/pitcher_projections.json", false),
          loadJson("/data/mlb/latest/weather_game_time.json", false),
        ]);

        if (cancelled) return;

        setBatterData(safeArray(batters));
        setPitcherData(safeArray(pitchers));
        setBatterProjData(safeArray(batterProj));
        setPitcherProjData(safeArray(pitcherProj));
        setWeatherGameTimeData(safeArray(weatherGameTime));
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Failed to load MLB matchup data.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const batterProjLookup = useMemo(() => buildBatterProjectionLookup(batterProjData), [batterProjData]);
  const pitcherProjLookup = useMemo(() => buildPitcherProjectionLookup(pitcherProjData), [pitcherProjData]);
  const weatherLookup = useMemo(() => buildWeatherLookup(weatherGameTimeData), [weatherGameTimeData]);

  const enrichedBatters = useMemo(
    () => batterData.map((row) => attachBatterProjection(row, batterProjLookup)),
    [batterData, batterProjLookup]
  );

  const enrichedPitchers = useMemo(
    () => pitcherData.map((row) => attachPitcherProjection(row, pitcherProjLookup)),
    [pitcherData, pitcherProjLookup]
  );

  const games = useMemo(
    () =>
      buildGamesFromData({
        batterRows: enrichedBatters,
        pitcherRows: enrichedPitchers,
        weatherLookup,
      }),
    [enrichedBatters, enrichedPitchers, weatherLookup]
  );

  const filteredGames = useMemo(() => {
    const q = normalizeName(search);

    const base = !q
      ? games
      : games
          .map((game) => {
            const awayLineup = (game.away.lineup || []).filter((p) => normalizeName(p.name).includes(q));
            const homeLineup = (game.home.lineup || []).filter((p) => normalizeName(p.name).includes(q));

            const awayMatch =
              normalizeName(game.away.code).includes(q) ||
              normalizeName(game.away.meta.name).includes(q) ||
              normalizeName(game.away.pitcherName).includes(q);

            const homeMatch =
              normalizeName(game.home.code).includes(q) ||
              normalizeName(game.home.meta.name).includes(q) ||
              normalizeName(game.home.pitcherName).includes(q);

            if (!awayMatch && !homeMatch && !awayLineup.length && !homeLineup.length) return null;

            return {
              ...game,
              away: {
                ...game.away,
                lineup: awayMatch && !awayLineup.length ? game.away.lineup : awayLineup,
              },
              home: {
                ...game.home,
                lineup: homeMatch && !homeLineup.length ? game.home.lineup : homeLineup,
              },
            };
          })
          .filter(Boolean);

    return sortGames(base, sortBy);
  }, [games, search, sortBy]);

  const heroStats = useMemo(() => {
    const allLineups = filteredGames.flatMap((g) => [...(g.away.lineup || []), ...(g.home.lineup || [])]);
    const avgTotal = average(
      filteredGames.flatMap((g) => [num(g.away.total), num(g.home.total)]).filter((v) => v !== null)
    );
    const maxWind = Math.max(0, ...filteredGames.map((g) => num(g.extra?.weather?.wind) || 0));

    return {
      gameCount: filteredGames.length,
      playerCount: allLineups.length,
      avgTotal,
      maxWind,
    };
  }, [filteredGames]);

  return (
    <div className={UI.page}>
      <div className={UI.shell}>
        <div className={UI.top}>
          <div className={UI.topInner}>
            <div className="min-w-0">
              <div className={UI.eyebrow}>MLB Slate</div>
              <h1 className={UI.headline}>Tonight&apos;s Matchups</h1>
              <p className={UI.sub}>
                Compare both lineups, check key bats and pitchers, and get a quick feel for each game in one place.
              </p>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={UI.chip}>{heroStats.gameCount} Games</span>
                <span className={UI.chip}>{heroStats.playerCount} Players</span>
                <span className={UI.chip}>Avg Team Total {fmt1(heroStats.avgTotal)}</span>
              </div>
            </div>

            <div className={UI.section + " p-3 md:w-[460px] xl:w-[500px]"}>
              <div className="space-y-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <div className={UI.soft + " p-2"}>
                    <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      View
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <ToolbarButton active={metricMode === "proj"} onClick={() => setMetricMode("proj")}>
                        DFS View
                      </ToolbarButton>
                      <ToolbarButton active={metricMode === "stats"} onClick={() => setMetricMode("stats")}>
                        Stats View
                      </ToolbarButton>
                    </div>
                  </div>

                  <div className={UI.soft + " p-2"}>
                    <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Sort
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <ToolbarButton active={sortBy === "time"} onClick={() => setSortBy("time")}>
                        Time
                      </ToolbarButton>
                      <ToolbarButton active={sortBy === "total"} onClick={() => setSortBy("total")}>
                        Total
                      </ToolbarButton>
                    </div>
                  </div>
                </div>

                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search for a team, hitter, or pitcher..."
                  className={UI.search}
                />
              </div>

              </div>
            </div>
          </div>

        {loading && (
          <div className="rounded-[20px] border border-white/10 bg-slate-950/70 p-8 text-base text-slate-300">
            Loading MLB matchup data...
          </div>
        )}

        {!loading && error && (
          <div className="rounded-[20px] border border-red-500/20 bg-red-950/30 p-8 text-base text-red-200">
            {error}
          </div>
        )}

        {!loading && !error && !filteredGames.length && (
          <div className="rounded-[20px] border border-white/10 bg-slate-950/70 p-8 text-base text-slate-300">
            No matchup data found.
            <div className="mt-3 space-y-1 text-sm text-slate-400">
              <div>/public/data/mlb/latest/batter_data.json</div>
              <div>/public/data/mlb/latest/pitcher_data.json</div>
              <div>/public/data/mlb/latest/batter_projections.json</div>
              <div>/public/data/mlb/latest/pitcher_projections.json</div>
              <div>/public/data/mlb/latest/weather_game_time.json</div>
            </div>
          </div>
        )}

        {!loading && !error && filteredGames.length > 0 && (
          <div className={UI.board}>
            {filteredGames.map((game) => (
              <GameCard
                key={game.id}
                game={game}
                metricMode={metricMode}
                onSelectHitter={(player) =>
                  setSelectedDetail({
                    type: "hitter",
                    player,
                    oppPitcherHand:
                      player?.Pitcher
                        ? (
                            enrichedPitchers.find(
                              (p) => normalizeName(p?.player) === normalizeName(player?.Pitcher)
                            )?.Hand ||
                            enrichedPitchers.find(
                              (p) => normalizeName(p?.player) === normalizeName(player?.Pitcher)
                            )?.proj?.H ||
                            player?.PH
                          )
                        : player?.PH,
                  })
                }
                onSelectPitcher={(pitcher) =>
                  setSelectedDetail({
                    type: "pitcher",
                    player: pitcher,
                  })
                }
              />
            ))}
          </div>
        )}

        <Modal open={!!selectedDetail} onClose={() => setSelectedDetail(null)}>
          {selectedDetail?.type === "hitter" ? (
            <HitterModal batter={selectedDetail?.player} oppPitcherHand={selectedDetail?.oppPitcherHand} />
          ) : selectedDetail?.type === "pitcher" ? (
            <PitcherModal pitcher={selectedDetail?.player} />
          ) : null}
        </Modal>
      </div>
    </div>
  );
}